<template>
  <v-flex xs12 sm10 md8>
    <v-card class="py-4">
      <v-flex xs12 pb-3 px-4 v-if="err">
        <v-alert :value="true" type="error">{{err}}</v-alert>
      </v-flex>
      <v-flex xs12 pb-3 px-4 v-if="success">
        <v-alert :value="true" type="success">{{success}}</v-alert>
      </v-flex>
      <v-form v-model="valid">
        <v-flex xs12 md6 px-4 v-if="!isLocalAdmin">
          <v-autocomplete
            :items="companies"
            :label="$lang.actions.selectCompany"
            v-model="account.companyId"
            item-text="name"
            item-value="id"
            required
            :readonly="isEdit"
            :loading="companyLoading"
            :rules="[v => !!v || `${$lang.errors.missingCompany}`]"
          />
        </v-flex>
        <v-layout row wrap class="px-2">
          <v-flex xs12 md6 px-3>
            <v-text-field
              :label="$lang.labels.holder"
              v-model="account.name"
              required
              :rules="[v => !!v || `${$lang.errors.missingHolder}`]"
            />
          </v-flex>
          <v-flex xs12 md6 px-3>
            <v-text-field
              :label="$lang.labels.username"
              v-model="account.userName"
              :hint="$lang.labels.lowerCase"
              @blur="account.userName = account.userName.toLowerCase().trim()"
              required
              :rules="[v => !!v || `${$lang.errors.missingUserName}`]"
            />
          </v-flex>
          <v-flex xs12 md6 px-3>
            <v-text-field
              :label="$lang.labels.email"
              type="email"
              v-model="account.email"
            />
          </v-flex>
          <v-flex xs12 md6 px-3 v-if="lastLoggedInOn">
            <v-text-field
              :label="$lang.labels.lastLoggedinOn"
              v-model="lastLoggedInOn"
              readonly
            />
          </v-flex>
        </v-layout>
        <v-layout row wrap class="px-2">
          <v-flex xs12 md6 px-3>
            <v-text-field
              :label="$lang.labels.maxDiscount"
              v-model="account.maxDiscountPercentage"
              type="number"
              min="0"
              step="0.05"
              required
              :rules="[v => checkMaxDiscountPercentage(v) || `${$lang.errors.missingDicount}`]"
            />
          </v-flex>
          <v-flex xs12 md6 px-3>
            <v-autocomplete
              :items="mappedPrinters"
              :label="$lang.actions.selectDefaultPrinter"
              v-model="account.defaultPrinter"
              required
              :disabled="mappedPrinters.lenght === 0"
              :loading="printersLoading"
              :rules="[v => !!v  || `${$lang.errors.missingDefaultPrinter}`]"
            />
          </v-flex>
        </v-layout>
        <v-flex xs12 px-3 mb-3>
          <p class="pl-2 mb-0 pb-0">{{ $lang.header.roleManagement}}:</p>
          <div class="d-inline-flex">
            <v-btn
              class="primary mx-2"
              @click="manageRoles('none')">
              {{ $lang.labels.none}}
            </v-btn>
            <v-btn
              class="primary mx-2"
              @click="manageRoles('user')">
              {{ $lang.labels.atUser}}
            </v-btn>
            <v-btn
              class="primary mx-2"
              @click="manageRoles('admin')">
              {{ $lang.labels.atLocalAdmin}}
            </v-btn>
          </div>
        </v-flex>
        <v-flex xs12 px-4>
          <v-select
            small-chips
            deletable-chips
            multiple
            :label="$lang.actions.selectRoles"
            :loading="rolesLoading"
            :items="sortedRoles"
            v-model="selectedRoles"
          />
        </v-flex>
        <v-layout row wrap>
          <v-flex xs6 px-4>
            <v-text-field
              :label="$lang.labels.apiKey"
              v-model="account.apiKey"
            />
          </v-flex>
          <v-flex xs6 px-4>
            <v-text-field
              :label="$lang.labels.nfcToken"
              v-model="account.nfcToken"
            />
          </v-flex>
        </v-layout>
        <v-flex xs12 px-4>
          <v-textarea
            :label="$lang.labels.notes"
            v-model="account.notes"
            auto-grow
            rows="1"
          />
        </v-flex>
        <v-layout row wrap justify-space-between class="px-2">
          <v-flex xs12 md6 px-3>
            <v-text-field
              v-if="isEdit && createdOn"
              :label="$lang.labels.created"
              v-model="createdOn"
              readonly
            />
          </v-flex>
          <v-flex xs12 md6 px-3>
            <v-text-field
              v-if="isEdit && modifiedOn"
              :label="$lang.labels.modified"
              v-model="modifiedOn"
              readonly
            />
          </v-flex>
        </v-layout>
        <v-layout row wrap justify-space-between align-center class="px-2">
          <v-btn
            flat class="mx-3"
            @click="$router.push({ name: 'accounts' })">
            <v-icon>keyboard_arrow_left</v-icon>
            {{$lang.actions.close}}
          </v-btn>
          <div>
            <v-btn
              v-if="isEdit"
              class="primary mx-2"
              :disabled="passSnackbar || account.accountStatus === 'inactive'"
              @click="resetPassword">
              {{ $lang.actions.resetPassword}}
            </v-btn>
            <v-btn
              class="primary mx-3"
              :disabled="!valid || isPrinterActive || impactSnackbar"
              @click="submit()">
              {{ isEdit ? $lang.actions.update : $lang.actions.create}}
            </v-btn>
          </div>
        </v-layout>
      </v-form>
    </v-card>
    <div class="snackbar-wrap">
      <v-snackbar
        v-model="passSnackbar"
        bottom
        :timeout="timeout"
        color="success"
        multi-line
        class="mx-3"
      >
        {{ $lang.labels.yourPassword }}{{ password }}
        <v-btn
          flat
          @click="passSnackbar = false"
        >
          {{$lang.actions.close}}
        </v-btn>
      </v-snackbar>
      <v-snackbar
        v-model="impactSnackbar"
        bottom
        :timeout="timeout"
        color="error"
        multi-line
        class="mx-3"
      >
        {{ $lang.labels.accountImpactWarning }}
        <v-btn
          flat
          @click="impactSnackbar = false"
        >
          {{$lang.actions.close}}
        </v-btn>
      </v-snackbar>
    </div>
  </v-flex>
</template>

<script>
  import {DateTime} from 'luxon';

  export default {
    data: () => ({
      err: '',
      success: '',
      timeout: 0,
      passSnackbar: false,
      impactSnackbar: false,
      valid: false,
      isEdit: false,
      companies: [],
      printers: [],
      roles: [], /* all roles fetched by comapny ID */
      companyLoading: true,
      printersLoading: false,
      rolesLoading: true,
      isPrinterActive: false,
      account: {
        companyId: null,
        name: null,
        email: '',
        userName: null,
        defaultPrinter: null,
        maxDiscountPercentage: null,
        notes: '',
        apiKey: '',
        nfcToken: '',
      },
      selectedRoles: [],
      lastLoggedInOn: null,
      createdOn: '',
      modifiedOn: '',
      password: null,
      isLocalAdmin: false,
    }),
    watch: {
      'account.companyId': function(val) {
        this.fetchPrinters(val);
        /* fetch roles when user select company */
        this.fetchRoles(val);
      },
      'account.defaultPrinter': function(val) {
        const printer = this.printers.find(printer => printer.id === val);
        if (!printer) {
          this.isPrinterActive = false;
          return;
        }
        this.isPrinterActive = printer.printerStatus === 'acitve';
      },
      err(val) {
        if (val !== '') {
          setTimeout(() => { this.err = ''; }, 5000);
        }
      },
      success(val) {
        if (val !== '') {
          setTimeout(() => { this.success = ''; }, 5000);
        }
      },
    },
    computed: {
      mappedPrinters() {
        return this.printers.map(printer => ({
          text: `${printer.name} (${printer.printerStatus})`,
          value: printer.id,
        }));
      },
      /* this are :items from select role field
      all roles that are fatched by company ID get sorted alphabetically */
      sortedRoles() {
        return this.roles = this.roles.sort();
      },
    },
    methods: {
      manageRoles(type) {
        this.selectedRoles = [];
        const userRoles = [ 'ROLE_MOON_DRAWER_HANDLER', 'ROLE_MOON_ORDER_CREATOR', 'ROLE_MOON_REPORTS_CREATOR', 'ROLE_MOON_INVOICE_REPRINT', 'ROLE_MOON_CONTACT_CREATOR' ];
        const adminRoles = ['ROLE_MOON_CONTACT_CREATOR', 'ROLE_MOON_CONTACT_DELETE', 'ROLE_MOON_DRAWER_HANDLER', 'ROLE_MOON_EXPORT_ADMIN', 'ROLE_MOON_INVOICE_CANCEL', 'ROLE_MOON_INVOICE_REPRINT', 'ROLE_MOON_ORDER_CREATOR', 'ROLE_MOON_REPORTS_CREATOR', 'ROLE_SUN_LOCAL_ADMIN', 'ROLE_MOON_PAYMENT_ADMIN', 'ROLE_MOON_PAYMENT_APPROVED'];
        if (type === 'user') this.selectedRoles = [...userRoles];
        if (type === 'admin') this.selectedRoles = [...adminRoles];
      },
      checkMaxDiscountPercentage(v) {
        return !(Number(v) < 0 || v === null || v === '' || v === undefined);
      },
      formatDate(date) {
         return DateTime.fromISO(date).toFormat('yyyy-MM-dd HH:mm:ss');
      },
      init(){
        return new Promise((resolve) => {
          this.$http.get('company')
            .then((res) => {
              resolve()
              this.companyLoading = false;
              if (res.body.status === 'Success') {
                this.companies = res.body.companies;
                return;
              }
              this.err = res.body.statusText;
            }, (err) => {
              resolve()
              this.companyLoading = false;
              this.err = err.body.statusText || err.body.message;
            });
        });
      },
      /* fetch roles by company ID */
      fetchRoles(id) {
        if(id === null) {
          this.rolesLoading = false;
          return;
        }
        this.rolesLoading = true;
        this.$http.post('account/fetchroles', {companyId: id})
          .then((res) => {
            if (res.body.status === 'Success') {
              this.rolesLoading = false;
              this.roles = res.body.roles;
              if (this.roles.length === 0) {
                this.rolesLoading = false;
              }
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.rolesLoading = false;
            this.err = err.body.statusText || err.body.message;
          });
      },
      fetchPrinters(id) {
        this.printersLoading = true;
        this.printers = [];
        this.$http.get(`printer/fetchallforcompany/${id}`)
          .then((res) => {
            this.printersLoading = false;
            if (res.body.status === 'Success') {
              this.printers = res.body.printers;
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.printersLoading = false;
            this.err = err.body.statusText || err.body.message;
          });
      },
      resetPassword() {
        this.$http.post(`account/resetpassword/${this.$route.params.id}`, {})
          .then((res) => {
            if (res.body.status === 'Success') {
              this.passSnackbar = true;
              this.password = res.body.newPassword;
              return;
            }
            this.err = res.body.statusText
          }, (err) => {
            this.err = err.body.statusText || err.body.message;
          });
      },
      submit() {
        this.accountUpdate()
          .then(res => res ? this.rolesUpdate() : null)
      },
      rolesUpdate() {
        return new Promise((resolve) => {
          const roles = {
            add: [],
            delete: [],
          }
          this.account.roles.forEach((role) => {
            if (!this.selectedRoles.includes(role)) {
              roles.delete.push(role);
            }
          });
          this.selectedRoles.forEach((role) => {
            if (!this.account.roles.includes(role)) {
              roles.add.push(role);
            }
          });
          if (roles.add.length === 0 && roles.delete.length === 0) {
            resolve(true);
            return;
          }
          this.$http.post(`account/manageroles/${this.$route.params.id}`, { roles })
            .then((res) => {
              if (res.body.status === 'Success') {
                this.account.roles = this.selectedRoles;
                const currentUser = localStorage.currentUser ? JSON.parse(localStorage.currentUser) : null;
                if (currentUser.name === this.account.name) this.$auth.rolesCookieUpdate(this.selectedRoles);
                resolve(true);
                return;
              }
              this.err = res.body.statusText;
              resolve(false);
            })
            .catch((err) => {
              this.err = err.body.statusText || err.body.message;
              resolve(false);
            });
        });
      },
      accountUpdate() {
        return new Promise((resolve) => {
          let url = this.isEdit ? `account/update/${this.$route.params.id}` : 'account/create';
          let accountData = this.account;
          if (!this.isEdit) {
            Object.assign(accountData, { roles: this.selectedRoles });
          }
          this.$http.post(url, accountData)
            .then((res) => {
              if (res.body.status === 'Success') {
                if (res.body.password) {
                  this.password = res.body.password;
                }
                if (this.passSnackbar === false) {
                  this.passSnackbar = this.isEdit ? false : true;
                }
                const roles = JSON.parse(this.$cookie.get('roles') || '[]');
                if (roles.includes('ROLE_SUN_LOCAL_ADMIN')) {
                  this.impactSnackbar = true;
                }
                this.success = this.isEdit ? 'Account updated successfuly' : 'Account created successfuly';
                resolve(true);
                return;
              }
              resolve(false);
              this.err = res.body.statusText;
            }, (err) => {
              resolve(false);
              this.err = err.body.statusText || err.body.message;
            });
        })
      },
    },
    created() {
      this.init()
        .then(() => {
          const roles = JSON.parse(this.$cookie.get('roles') || '[]');
          this.isLocalAdmin = roles.includes('ROLE_SUN_LOCAL_ADMIN');
          if (this.isLocalAdmin) {
            if (this.companies.length === 1) this.account.companyId = this.companies[0].id;
          }
          if (this.$route.params.id) {
            this.isEdit = true;
            this.$http.get(`account/${this.$route.params.id}`)
              .then((res) => {
                if (res.body.status === 'Success') {
                  this.account = res.body.account;
                  this.selectedRoles = this.account.roles.slice();
                  this.createdOn = this.formatDate(this.account.createdOn);
                  if(this.account.modifiedOn){
                    this.modifiedOn = this.formatDate(this.account.modifiedOn);
                  }
                  if(this.account.lastLoggedInOn){
                    this.lastLoggedInOn = this.formatDate(this.account.lastLoggedInOn);
                  }
                  if (this.isLocalAdmin) {
                    if (this.companies.length === 1) this.account.companyId = this.companies[0].id;
                  }
                  return;
                }
                this.err = res.body.statusText
              }, (err) => {
                this.err = err.body.statusText || err.body.message;
              })
            }
        });
    },
  };
</script>

<style lang="scss">
  .snackbar-wrap {
    position: fixed;
    bottom: 0;
    height: auto;
    width: 100%;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    .v-snack {
      position: relative;
    }
    .v-snack__content {
      height: auto;
      min-height: 80px;
    }
    .v-snack--multi-line .v-snack__content {
      height: auto;
      min-height: 80px;
    }
    .v-snack__wrapper {
      height: 100%;
    }
  }
</style>



// WEBPACK FOOTER //
// AccountDetails.vue?4cce58e6